import type * as THREE from 'three';
import { clamp } from '../core/math';
import type { Random } from '../core/Random';
import { buildCar, buildSemi, type BuiltCar, type CarKind } from './builders/vehicles';

export type TrafficKind = 'car' | 'semi';

interface Vehicle {
  car: BuiltCar;
  kind: TrafficKind;
  dir: 1 | -1;
  z: number;
  speed: number;
  cruise: number;
  honkTimer: number;
}

export interface TrafficHit {
  dir: number;
  kind: TrafficKind;
  speed: number;
}

/** Eastbound in the south lane, westbound in the north lane. */
export const LANES = [
  { dir: 1, z: 63.5 },
  { dir: -1, z: 58.5 },
] as const;

/** Vehicles drive from one end of this span to the other, then queue back up off-screen. */
const SPAN = 160;
const HAULERS = [
  { bg: '#f4f1e8', fg: '#1d4e9e', lines: ['SAV-MOR', 'Low Prices. Lower Standards.'] },
  { bg: '#ffffff', fg: '#c62828', lines: ["HANK'S HAULIN'", 'If It Fits, It Ships. Mostly.'] },
  { bg: '#1f6b3a', fg: '#ffffff', lines: ['PIGGLY JIGGLY', 'Fresh Meat · Questionable Dates'] },
] as const;

/**
 * Highway 41. Cars and 18-wheelers keep to their lanes, follow at a distance instead
 * of driving through each other, and will not stop for a pedestrian. Semis lean on the
 * air horn when you're in their way.
 */
export class Traffic {
  private readonly vehicles: Vehicle[] = [];

  constructor(
    parent: THREE.Object3D,
    private readonly rng: Random,
  ) {
    const cars: Array<[CarKind, string]> = [
      ['pickup', '#b33a2b'],
      ['sedan', '#d9d4c7'],
      ['van', '#3b5b8c'],
      ['pickup', '#1f1f1f'],
      ['sedan', '#6a7f4f'],
      ['pickup', '#e0dccf'],
    ];
    LANES.forEach((lane, li) => {
      const roster: Array<BuiltCar & { kind: TrafficKind }> = [];
      for (let i = 0; i < 3; i++) {
        const [kind, color] = cars[li * 3 + i] as [CarKind, string];
        roster.push({ ...buildCar({ kind, color }), kind: 'car' });
      }
      const hauler = HAULERS[li % HAULERS.length]!;
      roster.splice(1 + li, 0, {
        ...buildSemi(li ? '#2b4f8a' : '#a3262a', {
          w: 1024,
          h: 224,
          bg: hauler.bg,
          fg: hauler.fg,
          gap: 4,
          lines: [
            { text: hauler.lines[0], size: 130, font: 'Bangers' },
            { text: hauler.lines[1], size: 54, font: 'Anton', color: '#333' },
          ],
        }),
        kind: 'semi',
      });
      roster.forEach((car, i) => {
        const v: Vehicle = {
          car,
          kind: car.kind,
          dir: lane.dir,
          z: lane.z,
          cruise: 0,
          speed: 0,
          honkTimer: 0,
        };
        v.cruise = this.cruiseFor(v);
        v.speed = v.cruise;
        // Spread out along the road, nose to tail in driving order.
        car.group.position.set(lane.dir * (SPAN - 20 - i * 75), 0, lane.z);
        car.group.rotation.y = lane.dir > 0 ? 0 : Math.PI;
        car.group.traverse((o) => (o.userData.noBatch = true));
        parent.add(car.group);
        this.vehicles.push(v);
      });
    });
  }

  private cruiseFor(v: Vehicle): number {
    return v.kind === 'semi' ? this.rng.range(15, 18) : this.rng.range(15, 21);
  }

  /** Moves everything; returns true if a semi wants to honk at the point `near`. */
  update(dt: number, near: { x: number; z: number } | null): { honk: boolean } {
    let honk = false;
    for (const v of this.vehicles) {
      const p = v.car.group.position;
      // Follow whoever is ahead in this lane at a safe gap.
      let gap = Infinity;
      let leadSpeed = v.cruise;
      for (const o of this.vehicles) {
        if (o === v || o.dir !== v.dir) continue;
        const ahead = (o.car.group.position.x - p.x) * v.dir;
        if (ahead <= 0) continue;
        const g = ahead - (o.car.length + v.car.length) / 2;
        if (g < gap) {
          gap = g;
          leadSpeed = o.speed;
        }
      }
      const safe = 7 + v.speed * 0.9;
      const target = gap < safe ? Math.min(v.cruise, leadSpeed * clamp(gap / safe, 0, 1)) : v.cruise;
      v.speed += clamp(target - v.speed, -14 * dt, 4 * dt);
      if (gap < 0.5) v.speed = Math.min(v.speed, leadSpeed);
      v.speed = Math.max(0, v.speed);
      p.x += v.dir * v.speed * dt;
      // Off the far end: queue back up behind the start, with a fresh cruising speed.
      if (p.x * v.dir > SPAN) {
        let last = SPAN;
        for (const o of this.vehicles)
          if (o !== v && o.dir === v.dir) last = Math.min(last, o.car.group.position.x * v.dir);
        p.x = v.dir * Math.min(-SPAN, last - 30 - this.rng.range(0, 50));
        v.cruise = this.cruiseFor(v);
      }
      v.car.group.visible = Math.abs(p.x) < SPAN + 4;
      for (const w of v.car.wheels) w.rotation.y += (v.speed * dt) / 0.4;
      v.honkTimer -= dt;
      if (near && v.kind === 'semi' && v.honkTimer <= 0) {
        const ahead = (near.x - p.x) * v.dir;
        if (ahead > 0 && ahead < 40 && Math.abs(near.z - v.z) < 2.6) {
          v.honkTimer = 6;
          honk = true;
        }
      }
    }
    return { honk };
  }

  /** The vehicle touching a circle at (x, z), if any. */
  hit(x: number, z: number, r: number): TrafficHit | null {
    for (const v of this.vehicles) {
      const p = v.car.group.position;
      if (Math.abs(p.x - x) < v.car.length / 2 + r && Math.abs(p.z - z) < v.car.width / 2 + r)
        return { dir: v.dir, kind: v.kind, speed: v.speed };
    }
    return null;
  }
}
